import { api, ApiError } from './api';
import i18n from '@/app/i18n/config';
import type { UserRole } from '@/types';

export interface RegisterRequest {
  email: string;
  password: string;
  displayName?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
}

export interface Profile {
  id: string;
  email: string;
  displayName: string | null;
  role: UserRole;
  isActive: boolean;
  preferredLanguage?: string | null;
  preferredCurrency?: string | null;
  createdAt: string;
}

export interface UpdateProfileRequest {
  displayName?: string;
  password?: string;
  preferredLanguage?: string | null;
  preferredCurrency?: string | null;
  role?: UserRole;
}

export interface DevUpdateRoleRequest {
  role: UserRole;
}

function getErrorMessage(errorCode?: string): string {
  if (!errorCode) {
    return i18n.t('genericError', { ns: 'auth' });
  }
  
  const translationKey = `errors.${errorCode}`;
  const translated = i18n.t(translationKey, { ns: 'auth' });
  
  // If translation key is not found, i18next returns the key itself
  if (translated === translationKey) {
    return i18n.t('genericError', { ns: 'auth' });
  }
  
  return translated;
}

class AuthService {
  async register(data: RegisterRequest): Promise<AuthResponse> {
    try {
      return await api.post<AuthResponse>('/auth/register', data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(getErrorMessage(error.errorCode));
      }
      throw new Error(i18n.t('genericError', { ns: 'auth' }));
    }
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    try {
      return await api.post<AuthResponse>('/auth/login', data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(getErrorMessage(error.errorCode));
      }
      throw new Error(i18n.t('genericError', { ns: 'auth' }));
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors on logout
    }
  }

  async getProfile(): Promise<Profile> {
    try {
      return await api.get<Profile>('/auth/profile');
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(getErrorMessage(error.errorCode));
      }
      throw new Error(i18n.t('genericError', { ns: 'auth' }));
    }
  }

  async updateProfile(data: UpdateProfileRequest): Promise<Profile> {
    try {
      return await api.patch<Profile>('/auth/profile', data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(getErrorMessage(error.errorCode));
      }
      throw new Error(i18n.t('genericError', { ns: 'auth' }));
    }
  }

  async devUpdateRole(data: DevUpdateRoleRequest): Promise<Profile> {
    try {
      return await api.patch<Profile>('/auth/profile/dev-role', data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(getErrorMessage(error.errorCode));
      }
      throw new Error(i18n.t('genericError', { ns: 'auth' }));
    }
  }
}

export const authService = new AuthService();
